import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Inbox, Plus, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useApi from '../hooks/useApi';
import Button from '../components/ui/Button';

const Dashboard = () => {
  const { user } = useAuth();
  const { get } = useApi();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ dispatches: 0, receipts: 0 });
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await get('/dashboard/stats');
        const nextCounts = response?.data?.counts || response?.counts || {};
        if (!mounted) return;
        setCounts(prev => ({
          ...prev,
          dispatches: Number.isFinite(nextCounts.dispatches) ? nextCounts.dispatches : prev.dispatches,
          receipts: Number.isFinite(nextCounts.receipts) ? nextCounts.receipts : prev.receipts
        }));
        setLastUpdatedAt(new Date());
      } catch {
        void 0;
      }
    })();
    return () => {
      mounted = false;
    };
  }, [get]);

  return (
    <div className="space-y-5 min-w-0">
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-blue-600 rounded-[26px] p-4 sm:p-6 text-white shadow-[0_20px_45px_rgba(79,70,229,0.28)] relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-300/10 rounded-full blur-2xl" />
        <div className="relative z-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-100/90">Operations overview</p>
            <h2 className="mt-2 text-xl sm:text-2xl font-bold tracking-tight">Welcome back, {user?.name || 'User'}!</h2>
            <p className="mt-1 max-w-2xl text-sm text-indigo-100/90">
              Quick access to Diary and Dispatch registers, compliance activity, and office summaries.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => navigate('/diary/new')}
              className="bg-white text-indigo-700 hover:bg-indigo-50 shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Diary
            </Button>
            <Button
              onClick={() => navigate('/dispatch/new')}
              className="bg-white text-indigo-700 hover:bg-indigo-50 shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Dispatch
            </Button>
            <Button
              onClick={() => navigate('/reports')}
              className="bg-indigo-900/25 text-white border border-white/20 hover:bg-indigo-900/35 shadow-sm"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Open Reports
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-indigo-100 bg-indigo-50/80 px-4 py-3 text-xs font-medium text-indigo-800 shadow-sm">
        Last refreshed: {lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 min-w-0">
        <StatCard
          title="Total Dispatches"
          value={counts.dispatches}
          icon={Send}
          color="text-blue-600"
          bg="bg-blue-100"
          description="Outgoing letters"
        />
        <StatCard
          title="Total Diaries"
          value={counts.receipts}
          icon={Inbox}
          color="text-emerald-600"
          bg="bg-emerald-100"
          description="Incoming diaries"
        />
        <StatCard
          title="Total Case Load"
          value={counts.dispatches + counts.receipts}
          icon={BarChart3}
          color="text-amber-600"
          bg="bg-amber-100"
          description="Dispatch + Diary"
        />
        <div className="glass-card min-w-0 p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(15,23,42,0.09)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Registers</div>
              <div className="mt-1 text-xs text-slate-500">Open daily worklists</div>
            </div>
            <div className="rounded-xl bg-indigo-50 p-2 text-indigo-600">
              <BarChart3 className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-2.5">
            <Button variant="secondary" onClick={() => navigate('/diary')} className="justify-start">
              <Inbox className="w-4 h-4 mr-2" />
              Diary Register
            </Button>
            <Button variant="secondary" onClick={() => navigate('/dispatch')} className="justify-start">
              <Send className="w-4 h-4 mr-2" />
              Dispatch Register
            </Button>
          </div>
        </div>

        <div className="glass-card min-w-0 p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(15,23,42,0.09)] lg:col-span-2 xl:col-span-1">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">My Office</div>
              <div className="mt-1 max-w-[220px] truncate text-xs text-slate-500">{user?.officeName || '-'}</div>
            </div>
            <div className="rounded-xl bg-slate-100 p-2 text-slate-600">
              <Send className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Role</div>
              <div className="mt-2 text-sm font-semibold text-slate-900 truncate">{user?.role || '-'}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Office Code</div>
              <div className="mt-2 text-sm font-semibold text-slate-900 truncate">{user?.officeCode || '-'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color, bg, trend, description }) => (
  <div className="glass-card group relative min-w-0 overflow-hidden p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(15,23,42,0.09)]">
    <div className="absolute -right-3 -top-4 text-indigo-100/80 opacity-70">
      <Icon className={`w-20 h-20 ${color}`} />
    </div>

    <div className="relative z-10">
      <div className="flex items-center justify-between mb-5">
        <div className={`inline-flex rounded-xl p-3 shadow-sm ${bg}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        {trend && (
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${trend.startsWith('+') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {trend}
          </span>
        )}
      </div>

      <div>
        <p className="text-sm font-medium text-slate-500 mb-2">{title}</p>
        <h3 className="text-3xl font-bold tracking-tight text-slate-900">{value}</h3>
      </div>

      {(description || trend) && (
        <div className="mt-4 flex items-center gap-1.5 border-t border-slate-100 pt-3 text-xs text-slate-400">
          {trend && (
            <span className={`font-semibold ${trend.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
              {trend}
            </span>
          )}
          <span>{description || 'vs last month'}</span>
        </div>
      )}
    </div>
  </div>
);

export default Dashboard;
